export const fr = {
  game: {
    title: "On joue ?",
    description: "Sautez à travers mes compétences et projets dans ce mini-jeu amusant. Pourrez-vous tout débloquer ?",
    pressToStart: "Appuyez sur ESPACE ou tapez pour commencer",
    instructions: "Sautez par-dessus les bugs pour débloquer des compétences et des projets !",
    gameOver: "Partie terminée !",
    finalScore: "Score final",
    unlockedSkills: "Compétences débloquées",
    unlockedProjects: "Projets débloqués",
    playAgain: "Rejouer",
    contactMe: "Me contacter"
  },
  footer: {
    tagline: "Développeur Full Stack passionné par la création de solutions élégantes à des problèmes complexes.",
    navigation: {
      title: "Navigation",
      home: "Accueil",
      about: "À propos",
      skills: "Compétences",
      projects: "Projets"
    },
    contact: {
      title: "Contact",
      email: "Email",
      github: "GitHub",
      linkedin: "LinkedIn"
    },
    legal: {
      title: "Légal",
      privacy: "Politique de confidentialité",
      terms: "Conditions d'utilisation"
    },
    copyright: "© 2024 Moustapha Sambe. Tous droits réservés."
  },
  blog: {
    title: "Blog",
    subtitle: "Je partage mes réflexions et expériences en développement web",
    readMore: "Lire la suite",
    loading: "Chargement des articles...",
    error: "Erreur lors du chargement des articles",
    noPosts: "Aucun article disponible pour le moment.",
    posts: {
      webApp: {
        title: "Créer des Applications Web Modernes avec React",
        summary: "Un guide complet pour construire des applications web performantes et évolutives avec React et les outils frontend modernes.",
        category: "Développement Web"
      },
      cicd: {
        title: "Pipelines CI/CD pour Applications Modernes",
        summary: "Explorer les meilleures pratiques pour implémenter des workflows d'intégration et de déploiement continus pour vos projets.",
        category: "DevOps"
      },
      mobile: {
        title: "Du Web au Mobile : Développement Cross-Platform",
        summary: "Comment utiliser vos compétences en développement web pour créer des applications mobiles natives pour iOS et Android.",
        category: "Développement Mobile"
      }
    },
    labels: {
      comingSoon: "Bientôt disponible",
      futurePost: "Article à venir"
    }
  },
  nav: {
    home: "Accueil",
    about: "À propos",
    skills: "Compétences",
    projects: "Projets",
    contact: "Contact"
  },
  hero: {
    greeting: "Bonjour, je suis",
    role: "Data Engineer & Développeur Full Stack",
    roles: [
      "Data Engineer",
      "Passionné IA",
      "Développeur Full Stack",
      "Passionné DevOps",
      "Développeur de pipelines ETL",
      "Ingénieur intégration API",
      "Développeur Web"
    ],
    description: "Je crée des expériences numériques exceptionnelles qui rendent la vie plus simple et plus agréable.",
    cta: "Me contacter",
    scrollDown: "Défiler vers le bas",
    viewProjects: "Voir les Projets"
  },
  about: {
    title: "À Propos de Moi",
    description: "Un ingénieur de données passionné avec une solide base en technologies de données et développement full stack. J'aime transformer des problèmes complexes en solutions simples, belles et intuitives.",
    yearsExperience: "Années",
    passionate: "Étudiant en Master Data Engineering & Intelligence Artificielle à l'ESGI Paris, passionné par la data, l'intelligence artificielle et les systèmes distribués.",
    currentStudy: "Je suis actuellement à la recherche d'une alternance en Data Analyst, Data Scientist ou Intelligence Artificielle à partir de septembre 2026, pour une durée de 12 à 24 mois avec un rythme de 3 semaines en entreprise et 1 semaine en école.",
    hobbies: "Je possède une expérience en intégration de données, développement de pipelines ETL et conception d'API sécurisées avec MuleSoft. Curieux, autonome et rigoureux, j'aime concevoir des solutions data intelligentes mêlant Machine Learning, visualisation de données et automatisation.",
    languages: "Langues",
    languageNames: "Français (Courant), Anglais (Avancé)",
    downloadCV: "Télécharger CV"
  },
  skills: {
    title: "Mes Compétences",
    subtitle: "Une boîte à outils complète de technologies et méthodologies que j'ai maîtrisées tout au long de mon parcours.",
    sections: {
      programmingLanguages: "Langages de Programmation",
      dataAndAI: "Data & IA",
      webTechnologies: "Technologies Web",
      devOpsAndCloud: "DevOps & Cloud",
      databases: "Bases de Données",
      softSkills: "Compétences Générales"
    },
    softSkillItems: {
      teamwork: "Esprit d'équipe",
      autonomy: "Autonomie",
      curiosity: "Curiosité",
      rigor: "Rigueur",
      creativity: "Créativité",
      adaptability: "Adaptabilité",
      communication: "Communication"
    }
  },
  experience: {
    title: "Expérience Professionnelle",
    subtitle: "Mon parcours professionnel à travers différents rôles et responsabilités.",
    present: "Présent",
    items: {
      cap4lab: {
        period: "Octobre 2023 - Septembre 2025",
        title: "Ingénieur intégration de données",
        description: "Développement et orchestration de flux de données et APIs avec MuleSoft.",
        skills: [
          "Développement et orchestration de flux de données",
          "Conception d'API sécurisées sous MuleSoft",
          "Implémentation de pipelines d'intégration",
          "Tests avec MUnit",
          "CI/CD avec Jenkins et GitLab",
          "Déploiement sur CloudHub",
          "Transformation de données inter-applicatives",
          "TLS & sécurité API"
        ]
      }
    },
    roles: {
      apprenticeIT: "Apprenti Ingénieur Intégrateur",
      apprenticeTech: "Développeur Web",
      webDev: "Stagiaire Développeur Web",
      mobileDev: "Développeur Mobile",
      dataAnalyst: "Analyste de Données"
    },
    descriptions: {
      apprenticeIT: "Développement et intégration d'API avec Mule 4. Tests unitaires avec MUnit, documentation API, développement de politiques personnalisées Mule 4, conception RAML et versionnement Git.",
      apprenticeTech: "Développement d'une application FAQ pour apprenants et instructeurs. Construction de l'architecture et des interfaces, création de la base de données.",
      webDev: "Développement d'une application mobile FAQ avec React Native et Firebase. Création de l'interface utilisateur et implémentation des fonctionnalités interactives.",
      mobileDev: "Développement d'un jeu de plateau type dames sur Google Play Store. Développé initialement pour Android avec Java et Eclipse, en cours de redéveloppement avec React et Node.js pour iOS et Android.",
      dataAnalyst: "Projet d'analyse des ventes avec SQL et Power BI. Création de tableaux de bord interactifs pour visualiser les indicateurs de performance clés. Extraction, transformation et agrégation de données via SQL. Développement de visualisations avec Power BI et DAX."
    },
    skills: {
      projectManagement: "Gestion de Projet",
      networkSecurity: "Sécurité Réseau",
      fullStack: "Développement Full Stack",
      sql: "SQL",
      dbAdmin: "Administration de Base de Données",
      automation: "Automatisation",
      html: "HTML5",
      python: "Python",
      javascript: "JavaScript",
      paperjs: "Paper.js",
      java: "Java",
      reactjs: "React.js",
      nodejs: "Node.js",
      androidStudio: "Android Studio",
      docker: "Docker",
      git: "Git",
      agile: "Méthodologie Agile",
      teamwork: "Travail d'équipe",
      communication: "Communication",
      problemSolving: "Résolution de problèmes",
      adaptability: "Adaptabilité",
      creativity: "Créativité"
    }
  },
  projects: {
    title: "Mes Projets",
    subtitle: "Découvrez une sélection de mes derniers projets, mettant en valeur mes compétences et mon expérience.",
    viewProject: "Voir le Projet",
    liveDemo: "Démo en Direct",
    viewSourceCode: "Voir le code source",
    viewAllProjects: "Voir Tous les Projets sur GitHub",
    learnMore: "En savoir plus",
    fullscreen: "Voir en plein écran",
    items: {
      joj2026: {
        title: "Trafic urbain Dakar – JOJ 2026",
        description:
          "Projet d'analyse de données et de prédiction du trafic urbain utilisant Python et le Machine Learning afin d'anticiper les congestions pendant les JOJ 2026.",
        clientType: "Data & Machine Learning",
        imageAlt: "Aperçu projet JOJ 2026",
      },
      burnoutCheck: {
        title: "BurnoutCheck",
        description:
          "Application intelligente de prédiction du risque de burnout basée sur le Machine Learning et des données comportementales, avec explicabilité SHAP.",
        clientType: "IA & Data",
        imageAlt: "Aperçu BurnoutCheck",
      },
      socabeg: {
        title: "SOCABEG",
        description:
          "Site web professionnel pour une entreprise de BTP avec présentation des services, projets réalisés et système de demande de devis en ligne.",
        clientType: "BTP & construction",
        imageAlt: "Aperçu du site SOCABEG",
      },
      khalilCollection: {
        title: "Khalil Collection",
        description:
          "Boutique en ligne pour une marque de mode premium avec catalogue filtrable, commandes personnalisées et gestion des stocks intégrée.",
        clientType: "Mode & e-commerce",
        imageAlt: "Aperçu Khalil Collection",
      },
      noppalJaay: {
        title: "Noppal Jaay",
        description:
          "SaaS de commerce conversationnel permettant aux commerçants de créer une boutique en ligne, gérer leur catalogue et piloter leurs commandes avec intégration WhatsApp.",
        clientType: "SaaS & retail",
        imageAlt: "Aperçu Noppal Jaay",
      },
      magouGlow: {
        title: "MagouGlow Shop",
        description:
          "Boutique e-commerce premium spécialisée dans les produits skincare et beauté avec une expérience utilisateur moderne.",
        clientType: "Beauté & e-commerce",
        imageAlt: "Aperçu MagouGlow Shop",
      },
      sengeoMap: {
        title: "SengeoMap",
        description:
          "Application web cartographique interactive permettant la visualisation et l'exploration de données géographiques.",
        clientType: "Cartographie & data",
        imageAlt: "Aperçu SengeoMap",
      },
      yoonExpress: {
        title: "Yoon Express",
        description:
          "Plateforme de réservation de navettes interurbaines permettant la recherche, la réservation et le paiement sécurisé de trajets avec espaces utilisateur, chauffeur et administration.",
        clientType: "Mobilité & SaaS",
        imageAlt: "Aperçu Yoon Express",
      },
      rentyx: {
        title: "Rentyx",
        description:
          "Plateforme digitale de réservation et location en ligne avec une expérience utilisateur fluide et moderne.",
        clientType: "Location & réservation",
        imageAlt: "Aperçu Rentyx",
      },
    }
  },
  education: {
    title: "Formation",
    subtitle: "Mon parcours académique et mes qualifications.",
    items: {
      esgiMaster: {
        degree: "Master Data Engineering & Intelligence Artificielle",
        description: "Spécialisation en Data Engineering, IA, Machine Learning et systèmes distribués."
      },
      estiamMaster: {
        degree: "Master Full Lifecycle API Management",
        description: "Formation approfondie en développement, sécurité et gestion d'API."
      },
      epsiBachelor: {
        degree: "Bachelor DevOps CDA Full Stack",
        description: "Formation en développement full stack, DevOps et architecture logicielle."
      }
    }
  },
  // Section GitHub supprimée
  contact: {
    title: "Me Contacter",
    subtitle: "Vous avez un projet en tête ou vous voulez simplement dire bonjour ? N'hésitez pas à me contacter !",
    info: {
      title: "Informations de Contact",
      description: "N'hésitez pas à me contacter via l'un de ces canaux. Je suis toujours ouvert à la discussion de nouveaux projets, d'opportunités ou de partenariats.",
      location: {
        title: "Localisation",
        value: "Paris, 94800"
      },
      email: {
        title: "Email",
        value: "moustaphasambe719@gmail.com"
      },
      phone: {
        title: "Téléphone",
        value: "+33 7 53 84 81 57"
      },
      social: {
        title: "Me suivre"
      }
    },
    form: {
      title: "Envoyer un Message",
      name: {
        label: "Nom",
        placeholder: "Votre nom"
      },
      email: {
        label: "Email",
        placeholder: "Votre adresse email"
      },
      subject: {
        label: "Sujet",
        placeholder: "De quoi s'agit-il ?",
        options: {
          project: "Proposition de projet",
          job: "Opportunité d'emploi",
          collaboration: "Proposition de collaboration",
          question: "Question technique",
          feedback: "Retour sur le portfolio",
          other: "Autre sujet"
        }
      },
      message: {
        label: "Message",
        placeholder: "Votre message"
      },
      submit: {
        label: "Envoyer le Message",
        sending: "Envoi en cours..."
      }
    },
    toast: {
      success: {
        title: "Message envoyé !",
        description: "Merci de m'avoir contacté. Je vous répondrai bientôt !"
      },
      error: {
        title: "Erreur",
        description: "Il y a eu un problème lors de l'envoi de votre message. Veuillez réessayer."
      }
    }
  }
};
